import type { FC } from 'react';
import { memo, useCallback, useState } from 'react';
import { Box, Switch as MuiSwitch, Typography } from '@mui/material';
import type { SavedCreativeFilterSummaryRow } from '../../utils/savedCreativeFilters';
import DeleteSavedFilterDialog from './DeleteSavedFilterDialog';
import SavedFilterDropdown from './SavedFilterDropdown';

interface SaveFiltersControlProps {
  hasSaved: boolean;
  enabled: boolean;
  isDirty: boolean;
  savedAtLabel: string;
  savedSummaryLines: SavedCreativeFilterSummaryRow[];
  onEnable: () => void;
  onDisable: () => void;
  onUpdate: () => void;
  onClear: () => void;
}

const SaveFiltersControl: FC<SaveFiltersControlProps> = ({
  hasSaved,
  enabled,
  isDirty,
  savedAtLabel,
  savedSummaryLines,
  onEnable,
  onDisable,
  onUpdate,
  onClear,
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleOpenDeleteConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    onClear();
    setIsConfirmOpen(false);
  }, [onClear]);

  return (
    <>
      <DeleteSavedFilterDialog
        open={isConfirmOpen}
        savedSummaryLines={savedSummaryLines}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1.5 }}>
        <MuiSwitch
          size="small"
          checked={enabled}
          onChange={(_, nextChecked) => {
            if (nextChecked) {
              onEnable();
              return;
            }
            onDisable();
          }}
          inputProps={{
            'aria-label': 'Save filter',
          }}
        />

        {hasSaved ? (
          <SavedFilterDropdown
            label={savedAtLabel}
            isDirty={enabled && isDirty}
            disabled={!enabled}
            onUpdate={onUpdate}
            onDelete={handleOpenDeleteConfirm}
          />
        ) : (
          <Typography variant="body2">Save filter</Typography>
        )}
      </Box>
    </>
  );
};

export default memo(SaveFiltersControl);
